import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { ClubService } from '../shared/services/club.service';
import { ClubFlags, ClubTheme } from '../shared/models/clubs.models';

/**
 * Generic feature flag guard. Reads `flagKey` and/or `themeKey` from route data
 * and blocks access if the corresponding flag is falsy.
 *
 * For theme keys that default to `true` (e.g. `show_calendar`), use
 * `themeDefaultTrue` in route data to only block when explicitly set to `false`.
 *
 * Usage:
 *   {
 *     path: 'calendar',
 *     canActivate: [featureFlagGuard],
 *     data: { themeKey: 'show_calendar', themeDefaultTrue: true },
 *   }
 *
 *   {
 *     path: 'tournaments',
 *     canActivate: [featureFlagGuard],
 *     data: { themeKey: 'show_events_tab' },
 *   }
 *
 *   {
 *     path: 'workouts',
 *     canActivate: [featureFlagGuard],
 *     data: { flagKey: 'workouts' },
 *   }
 */
export const featureFlagGuard: CanActivateFn = (route) => {
  const clubService = inject(ClubService);
  const router = inject(Router);

  const flagKey = route.data['flagKey'] as keyof ClubFlags | undefined;
  const themeKey = route.data['themeKey'] as keyof ClubTheme | undefined;
  const themeDefaultTrue = route.data['themeDefaultTrue'] as boolean | undefined;

  if (flagKey) {
    const flagValue = clubService.flags()?.[flagKey];
    if (!flagValue) {
      router.navigate(['/']);
      return false;
    }
  }

  // `flagKeys`: ALL listed flags must be truthy (e.g. stripe_connect
  // additionally requires subscriptions). Backward-compatible — routes
  // that only set `flagKey` are unaffected.
  const flagKeys = route.data['flagKeys'] as (keyof ClubFlags)[] | undefined;
  if (flagKeys) {
    const flags = clubService.flags();
    for (const k of flagKeys) {
      if (!flags?.[k]) {
        router.navigate(['/']);
        return false;
      }
    }
  }

  // `anyFlags`: at least ONE listed flag must be truthy (e.g. the financial
  // reports serve clubs with subscriptions or with facilities).
  const anyFlags = route.data['anyFlags'] as (keyof ClubFlags)[] | undefined;
  if (anyFlags) {
    const flags = clubService.flags();
    if (!anyFlags.some((k) => !!flags?.[k])) {
      router.navigate(['/']);
      return false;
    }
  }

  if (themeKey) {
    const themeValue = clubService.theme()?.[themeKey];
    if (themeDefaultTrue) {
      // Only block when explicitly set to false
      if (themeValue === false) {
        router.navigate(['/']);
        return false;
      }
    } else {
      // Block when falsy (false, undefined, null)
      if (!themeValue) {
        router.navigate(['/']);
        return false;
      }
    }
  }

  return true;
};
